import { getChainId, simulateContract, writeContract } from "wagmi/actions";

import { wagmiConfig } from "@config";

import { findValidFee, getErrorResponse } from "./helpers";

import {
	lookupContractWithABI,
	type ChainId,
	type TokenDefinition,
} from "@bracket/addressbook";

import { getAmount } from "../../../contracts/helpers";
import { assertUserStaked } from "./assertUserStaked";
import { allow } from "./allow";
import { approve } from "./approve";
import type { ResolverResult } from "./types";

export declare type SwapProps = {
	/** the token we are swqapping into */
	token: TokenDefinition;
	/** the token we are swqapping FROM - WETH | ETH -- default is WETH */
	source: TokenDefinition;
	// /** the amount we are swapping in the source token value */
	amount_in: number;
	min_amount_out: number;
	chain?: "1" | "42161";
	/** the number of attempts previously tried, this is argued by THIS method as a fallback/retry. */
	attempt?: number;
};

export const swap = async (props: SwapProps): Promise<ResolverResult> => {
	const { source } = props;
	props.chain = props.chain || getChainId(wagmiConfig);
	switch (source.symbol) {
		case "ETH":
			return swapETH(props);
		case "WETH":
			return swapWETH(props);
		default:
			throw new Error(`cannot swap token ${source.symbol}`);
	}
};

const swapETH = async ({
	token,
	amount_in,
	min_amount_out,
	source,
	chain,
	attempt = 1,
}: SwapProps) => {
	try {
		const chainId = Number(chain) || getChainId(wagmiConfig);

		const fee = await findValidFee({ token, amount: amount_in });

		//@ts-ignore
		const [address, abi] = lookupContractWithABI(chainId, "EscrowRouter");

		const { request, result } = await simulateContract(wagmiConfig, {
			address,
			abi,
			functionName: "swapUniswapAndDeposit",
			args: [
				token.address,
				BigInt(min_amount_out * 1e18),
				fee,
			],
			value: BigInt(amount_in * 1e18),
		});

		const hash = await writeContract(wagmiConfig, request);

		if (hash) {
			await assertUserStaked();
			return {
				ok: true,
				status: "success",
				message: "Swap was successful",
				code: "SUCCESS",
				hash,
			};
		}

		throw new Error("transaction failed");
	} catch (err) {
		const response = getErrorResponse(err);
		const { retry } = response;
		if (retry && attempt <= 3) {
			return await swapETH({
				token,
				amount_in,
				min_amount_out,
				source,
				chain,
				attempt: attempt + 1,
			});
		}
		return response;
	}
};

const swapWETH = async ({
	token,
	amount_in,
	min_amount_out,
	source,
	chain,
	attempt = 1,
}: SwapProps) => {

	try {
		const chainId = Number(chain) || getChainId(wagmiConfig);
		const fee = await findValidFee({ token, amount: amount_in });
		const allowedAmount = await allow({
			token: source,
			contract: "EscrowRouter",
		});
		const requestedAmount = getAmount(source, amount_in);
		if (allowedAmount < requestedAmount) {
			await approve({
				token: source,
				amount: amount_in,
				contract: "EscrowRouter",
				subcall: true,
			});
		}
		//@ts-ignore
		const [address, abi] = lookupContractWithABI(chainId, "EscrowRouter");
		const {
			request,
			result,
		} = await simulateContract(wagmiConfig, {
			address,
			abi,
			functionName: "swapUniswapAndDeposit",
			args: [
				token.address,
				BigInt(amount_in * 1e18),
				BigInt(min_amount_out * 1e18),
				fee,
			],
		});
		const hash = await writeContract(wagmiConfig, request);

		if (hash) {
			await assertUserStaked();
			return {
				ok: true,
				status: "success",
				message: "Swap was successful",
				code: "SUCCESS",
				hash,
			};
		}
		throw new Error("transaction failed");
	} catch (err) {
		const response = getErrorResponse(err);
		const { retry } = response;
		if (retry && attempt <= 3) {
			return await swapWETH({
				token,
				amount_in,
				min_amount_out,
				source,
				chain,
				attempt: attempt + 1,
			});
		}
	}
};
